import { useMutation, useQuery } from "@tanstack/react-query";
import { fetch3, fetch4, fetch5 } from "../commonApis/commonApis";
import toast from "react-hot-toast";

const url = process.env.REACT_APP_BASE_URL;

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
}
 
export const useFetchNaqelCredentials = (companyName?: string, clientId?: string) => {
  return useQuery({
    queryKey: ["naqelCredentials", clientId],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/naqel/credentials?clientId=${clientId}`, "get");
      if (!res || !res.data) return null;
      return res.data;
    },
    // enabled: companyName?.toLowerCase() === "naqel express" || companyName?.toLowerCase() === "naqel" && !!clientId,
    enabled: companyName?.toLowerCase() === "naqel express" || companyName?.toLowerCase() === "naqel" || !!clientId,
    staleTime: 1000 * 60 * 5,
  });
};
 

export const useEditNaqelCredentialsMutation = () => {
  return useMutation({
    mutationFn: async (payload: {
      credentials: {
        userName: string;
        password: string;
        customerCode: string;
        branchCode: string;
      };
      clientId: string;
      connectionId: string;
    }) => {
      const res: ApiResponse = await fetch4(`${url}/api/naqel/connect`, payload);
      if (!res.success) throw res.message || "Failed to update Naqel credentials";
      return res.data;
    },
    onSuccess: () => console.log("✅ Naqel credentials updated"),
    onError: (err: any) => console.error("❌ Naqel edit failed:", err),
  });
};
 

 
export const useNaqelBulkForwardMutation = () => {
  return useMutation({
    mutationFn: async (payload: {
      orderIds: string[];
      noOfPieces?: number;
      productType?: string;
      dutyHandling?: string;
    }) => {
      if (!payload.orderIds?.length) throw new Error("No orders selected");
      if (payload.orderIds.length > 200) throw new Error("Maximum 200 orders per bulk request");
 
      const res = await fetch5(`${url}/api/bulk/naqel-bulk-forward`, "POST", payload);
      if (!res.success) throw res.message || "Naqel bulk forward failed";
      return res;
    },
    onSuccess: (res: any) => {
      console.log(
        `Naqel bulk forward job queued (${res?.data?.jobId ? `Job: ${res.data.jobId.slice(0, 8)}...` : ""}). You'll be notified when complete.`
      );
    },
    onError: (err: any) => toast.error(String(err) || "Naqel bulk forward failed"),
  });
};
 

 
export const useNaqelBulkLabelPrintMutation = () => {
  return useMutation({
    mutationFn: async (payload: {
      orderIds: string[];
      labelFormat?: string;
      labelsize?: string;
    }) => {
      if (!payload.orderIds?.length) throw new Error("No orders selected");
      if (payload.orderIds.length > 100) throw new Error("Maximum 100 labels per bulk request");
 
      const res = await fetch5(`${url}/api/bulk/naqel-bulk-label-print`, "POST", payload);
      if (!res.success) throw res.message || "Naqel bulk label print failed";
      return res;
    },
    onSuccess: (res: any) => {
      toast.success(
        `Naqel label Print request has been submitted successfully.`
      );
    },
    onError: (err: any) => toast.error(String(err) || "Naqel label print failed"),
  });
};

 
 
export const useNaqelJobStatus = (jobId?: string) => {
  return useQuery({
    queryKey: ["naqelJobStatus", jobId],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/bulk/jobs/${jobId}`, "get");
      if (!res?.data) return null;
      return res.data;
    },
    enabled: !!jobId,
    refetchInterval: (data: any) => {
      if (data?.status === "COMPLETED" || data?.status === "FAILED") return false;
      return 3000; 
    },
    staleTime: 0,
  });
};
 

export const useTrackNaqelShipment = () => {
  return useMutation({
    mutationFn: async (payload: { airwaybill: string; clientId: string }) => {
      const res: ApiResponse = await fetch4(`${url}/api/naqel/tracking`, payload);
      if (!res.success) throw res.message || "Naqel tracking failed";
      return res.data;
    },
    onSuccess: (data) => console.log("✅ Naqel tracking result:", data),
    onError: (error: any) => console.error("❌ Naqel tracking failed:", error),
  });
};
 

// Return / Reverse Shipment Mutations

export const useReverseNaqelShipmentMutation = () => {
  return useMutation({
    mutationFn: async (payload: { orderId: string; clientId: string }) => {
      const res: ApiResponse = await fetch5(`${url}/api/naqel/reverse-shipment/${payload.orderId}`, "POST", payload);
      if (!res.success) throw res.message || "Naqel reverse shipment failed";
      return res.data;
    },
    onSuccess: (data) => console.log("✅ Naqel reverse shipment result:", data),
    onError: (err: any) => toast.error(String(err) || "Naqel reverse shipment failed"),
  });
};


export const useBulkReverseNaqelShipmentMutation = () => {
  return useMutation({
    mutationFn: async (payload: { orderIds: string[]; clientId: string; noOfPieces?: number }) => {
      if (!payload.orderIds?.length) throw new Error("No orders selected for reversal");
      if (payload.orderIds.length > 50)
        throw new Error("Maximum 50 orders per bulk reverse request");
 
      const res = await fetch5(`${url}/api/naqel/bulk-reverse`, "POST", payload);
 
      // Hard failure: network error / server 4xx-5xx with success: false
      if (!res.success) {
        throw new Error(res.message || "Naqel bulk reverse shipment failed");
      }
 
      // Return the full response so the caller can inspect per-order results
      return res;
    },
    // onSuccess / onError handled at call-site in AllOrders.tsx
    onError: (err: any) =>
      console.error("❌ Naqel bulk reverse mutation error:", err),
  });
};
 